import { Fragment, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ScrollText, Search, ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import { queries } from '../lib/queries'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Skeleton } from '../components/ui/skeleton'

export const Route = createFileRoute('/admin/audit-logs')({
  head: () => ({
    meta: [{ title: 'Audit Logs | Admin | Unbreakable' }],
  }),
  component: AdminAuditLogsPage,
})

const ACTIONS = [
  'user.login', 'user.logout', 'user.register', 'user.password_change', 'user.email_change',
  'device.supervise', 'device.remove_supervision', 'device.enroll', 'device.unenroll', 'device.delete',
  'policy.create', 'policy.update', 'policy.delete', 'policy.apply', 'policy.remove',
  'schedule.create', 'schedule.update', 'schedule.delete',
  'account.locked', 'session.revoked',
  'admin.protected_apps.updated', 'admin.system_rule.created', 'admin.system_rule.updated', 'admin.system_rule.deleted',
  'admin.user_choice_option.created', 'admin.user_choice_option.updated', 'admin.user_choice_option.deleted',
]

const PAGE_SIZE = 50

function AdminAuditLogsPage() {
  const [filters, setFilters] = useState<{
    userId?: string; action?: string; resource?: string; startDate?: string; endDate?: string
  }>({})
  const [userEmail, setUserEmail] = useState('')
  const [offset, setOffset] = useState(0)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const queryParams = { ...filters, limit: PAGE_SIZE, offset }

  const { data, isLoading } = useQuery({
    queryKey: queries.admin.auditLogs(queryParams),
    queryFn: () => api.getAdminAuditLogs(queryParams),
  })

  const logs = data?.logs ?? []
  const total = data?.total ?? 0
  const hasMore = data?.hasMore ?? false

  const handleSearch = () => {
    setOffset(0)
    setFilters((prev) => ({ ...prev, userId: userEmail || undefined }))
    // Note: userId filter searches by email on the backend
  }

  const getActionBadgeVariant = (action: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (action.includes('delete') || action.includes('remove') || action.includes('revoke')) return 'destructive'
    if (action.includes('create') || action.includes('register') || action.includes('enroll')) return 'default'
    if (action.includes('update') || action.includes('change')) return 'secondary'
    return 'outline'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-500">View all system activity</p>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label>User Email</Label>
            <div className="flex gap-1">
              <Input
                placeholder="Search by email..."
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" size="sm" onClick={handleSearch} className="shrink-0">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label>Action</Label>
            <Select value={filters.action ?? '_all'} onValueChange={(v) => { setOffset(0); setFilters({ ...filters, action: !v || v === '_all' ? undefined : v }) }}>
              <SelectTrigger><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All actions</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Resource</Label>
            <Input placeholder="Resource ID..." value={filters.resource || ''} onChange={(e) => { setOffset(0); setFilters({ ...filters, resource: e.target.value || undefined }) }} />
          </div>
          <div>
            <Label>Start Date</Label>
            <Input type="date" value={filters.startDate || ''} onChange={(e) => { setOffset(0); setFilters({ ...filters, startDate: e.target.value || undefined }) }} />
          </div>
          <div>
            <Label>End Date</Label>
            <Input type="date" value={filters.endDate || ''} onChange={(e) => { setOffset(0); setFilters({ ...filters, endDate: e.target.value || undefined }) }} />
          </div>
        </div>
        {Object.values(filters).some(Boolean) && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={() => { setFilters({}); setUserEmail(''); setOffset(0) }}>Clear filters</Button>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
            <p className="text-sm text-gray-500">{total} event{total !== 1 ? 's' : ''} found</p>
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ScrollText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No audit logs found</p>
            <p className="text-sm">Try adjusting your filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>IP Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <Fragment key={log.id}>
                  <TableRow className="cursor-pointer hover:bg-gray-50" onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
                    <TableCell className="w-8">
                      {expandedRow === log.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm font-medium">{log.userEmail || log.userId || '—'}</span>
                        {log.userName && <span className="text-xs text-gray-500 block">{log.userName}</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getActionBadgeVariant(log.action)}>{log.action}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 font-mono max-w-[200px] truncate">
                      {log.resource || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{log.ipAddress || '—'}</TableCell>
                  </TableRow>
                  {expandedRow === log.id && (
                    <TableRow key={`${log.id}-details`}>
                      <TableCell colSpan={6} className="bg-gray-50">
                        <div className="p-4 space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-4">
                            <div><span className="font-medium text-gray-700">Log ID:</span> <span className="font-mono text-gray-500">{log.id}</span></div>
                            <div><span className="font-medium text-gray-700">User ID:</span> <span className="font-mono text-gray-500">{log.userId || '—'}</span></div>
                            {log.familyId && <div><span className="font-medium text-gray-700">Family ID:</span> <span className="font-mono text-gray-500">{log.familyId}</span></div>}
                            {log.schoolId && <div><span className="font-medium text-gray-700">School ID:</span> <span className="font-mono text-gray-500">{log.schoolId}</span></div>}
                            {log.staffUserId && <div><span className="font-medium text-gray-700">Staff User ID:</span> <span className="font-mono text-gray-500">{log.staffUserId}</span></div>}
                            <div><span className="font-medium text-gray-700">User Agent:</span> <span className="text-gray-500 break-all">{log.userAgent || '—'}</span></div>
                          </div>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div>
                              <span className="font-medium text-gray-700">Details:</span>
                              <pre className="mt-1 bg-white border border-gray-200 rounded-lg p-3 text-xs font-mono overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && logs.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <span className="text-sm text-gray-500">Showing {offset + 1}–{offset + logs.length} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>Previous</Button>
              <Button variant="outline" size="sm" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={!hasMore}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
